import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(getToken() ? null : false);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me when returning from a Google OAuth callback (handled by AuthCallback)
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) return;
    loadMe();
  }, [loadMe]);

  const setSession = useCallback((u, token) => {
    setToken(token);
    setUser(u);
  }, []);

  const login = useCallback(async (email, password, language) => {
    const { data } = await api.post("/api/auth/login", { email, password, language });
    setSession(data.user, data.token);
    return data.user;
  }, [setSession]);

  const accessCodeLogin = useCallback(async (identifier, code) => {
    const { data } = await api.post("/api/auth/access-code", { identifier: identifier.trim(), code: code.trim() });
    setSession(data.user, data.token);
    return data.user;
  }, [setSession]);

  const register = useCallback(async (payload) => {
    // Registration is non-enumerable: it returns a generic response (no token).
    const { data } = await api.post("/api/auth/register", payload);
    return data;
  }, []);

  const googleExchange = useCallback(async (session_id) => {
    const { data } = await api.post("/api/auth/google", { session_id });
    setSession(data.user, data.token);
    return data.user;
  }, [setSession]);

  const logout = useCallback(async () => {
    try { await api.post("/api/auth/logout"); } catch {}
    clearToken();
    setUser(false);
  }, []);

  const patchUser = useCallback((partial) => setUser((u) => ({ ...u, ...partial })), []);

  return (
    <AuthContext.Provider value={{ user, login, accessCodeLogin, register, googleExchange, logout, setSession, patchUser, reload: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
